import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import type { TMessage } from "@simplrsupport/headless-chat-web";
import { getMessageDataType, MESSAGE_DATA_TYPES, useChatSessionStateStore } from "@simplrsupport/headless-chat-web";
import { useMemo } from "react";
import { GuideCard, GuideSelectionCard } from "../components";
import type { CloseGuideSource } from "../types";
import { getGuideCardPropsFromTextMessage, getPropsFromGuideSelectionMessage } from "./logicBuilderResolvers";

export interface GuidedFlowMessageMapperProps {
    callSid?: string;
    clientChannelId?: string;
    latestMessage: TMessage | undefined;
    logger: Logger;
    partner: Partner;
    onGuideClose: (source: CloseGuideSource) => void;
    opened: boolean;
    requiresTopPadding: boolean;
    sendMessage: SendExpertAssistMessage;
    sessionId: string;
    ssoAccessToken: string;
}

export const GuidedFlowMessageMapper = ({
    callSid,
    clientChannelId,
    latestMessage,
    logger: loggerProp,
    partner,
    onGuideClose,
    opened,
    requiresTopPadding,
    sendMessage,
    sessionId,
    ssoAccessToken,
}: GuidedFlowMessageMapperProps) => {
    // moreTicketInfo is the logic builder's way of storing metadata about the session
    const { platformTicketId } = useChatSessionStateStore();

    const logger = useMemo(
        () => loggerProp.child({ module: "GuidedFlowMessageMapper", platformTicketId }),
        [loggerProp, platformTicketId],
    );

    if (!latestMessage) return null;

    const messageType = getMessageDataType(latestMessage) as MESSAGE_DATA_TYPES;
    switch (messageType) {
        case MESSAGE_DATA_TYPES.CUSTOM: {
            const { currentStepId } = getPropsFromGuideSelectionMessage(latestMessage);
            return (
                <GuideSelectionCard
                    callSid={callSid}
                    logger={logger}
                    onGuideClose={onGuideClose}
                    opened={opened}
                    stepId={currentStepId}
                />
            );
        }
        case MESSAGE_DATA_TYPES.TEXT: {
            const { actions, currentStep, dropdownInput, hasPreviousSteps, progress, prompt, tag, textInput, variant } =
                getGuideCardPropsFromTextMessage(latestMessage);

            return (
                <GuideCard
                    actions={actions}
                    callSid={callSid}
                    clientChannelId={clientChannelId}
                    currentStep={currentStep}
                    dropdownInput={dropdownInput}
                    hasPreviousSteps={hasPreviousSteps}
                    logger={logger}
                    onGuideClose={onGuideClose}
                    opened={opened}
                    partner={partner}
                    progress={progress}
                    prompt={prompt}
                    requiresTopPadding={requiresTopPadding}
                    sendMessage={sendMessage}
                    sessionId={sessionId}
                    ssoAccessToken={ssoAccessToken}
                    tag={tag}
                    textInput={textInput}
                    variant={variant}
                />
            );
        }
        default:
            logger.info({ latestMessage, messageType }, "Solve guide | Unsupported message type.");
            return undefined;
    }
};
